import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog/dialog';
import { Checkbox } from '@/components/ui/checkbox/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdownMenu/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { constants } from '@/lib/constants/constants';
import { Button } from '..';
import { useERSContext } from '@/context';

export const SettingsModal = ({ open, onOpenChange }) => {
  const { i18n, t } = useTranslation();
  const { selfProfileRef } = useERSContext();
  const username = selfProfileRef?.current?.username;

  const [language, setLanguage] = useState('en');
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [savedLanguage, setSavedLanguage] = useState('en');
  const [savedAutoTranslate, setSavedAutoTranslate] = useState(false);

  const isCheckboxDisabled = true;
  useEffect(() => {
    if (!username) return;

    const userSettings = JSON.parse(localStorage.getItem('userLanguages') || '{}');
    const userLang = userSettings[username] || 'en';
    const autoTranslateSetting = localStorage.getItem(`autoTranslate_${username}`) === 'true';

    setLanguage(userLang);
    setAutoTranslate(autoTranslateSetting);
    setSavedLanguage(userLang);
    setSavedAutoTranslate(autoTranslateSetting);

    i18n.changeLanguage(userLang);
  }, [username]);

  const handleSave = () => {
    if (!username) return;

    const userSettings = JSON.parse(localStorage.getItem('userLanguages') || '{}');
    userSettings[username] = language;

    localStorage.setItem('userLanguages', JSON.stringify(userSettings));
    localStorage.setItem(`autoTranslate_${username}`, autoTranslate);

    i18n.changeLanguage(language);

    setSavedLanguage(language);
    setSavedAutoTranslate(autoTranslate);

    onOpenChange(false);
  };

  const languages = [
    { code: 'en', label: constants.LANGUAGES.ENGLISH },
    { code: 'de', label: constants.LANGUAGES.DEUTSCH },
    { code: 'id', label: constants.LANGUAGES.BAHASA_INDONESIA },
    { code: 'es', label: constants.LANGUAGES.ESPAÑOL_LATINOAMÉRICA },
    { code: 'pt', label: constants.LANGUAGES.PORTUGUÊS_BRASIL },
    { code: 'el', label: constants.LANGUAGES.ΕΛΛΗΝΙΚΆ },
    { code: 'vi', label: constants.LANGUAGES.VIETNAMESE },
  ];

  const isChanged = language !== savedLanguage || autoTranslate !== savedAutoTranslate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[602px] h-[318px] p-4" style={{ maxWidth: '602px', height: '318px' }}>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text">{t('SETTINGS.TITLE')}</DialogTitle>
          <p className="text-sm text-muted">{t('SETTINGS.SUBTITLE')}</p>
        </DialogHeader>

        {/* Language Dropdown */}
        <div className="space-y-3">
          <p className="text-sm font-medium text">{t('SETTINGS.LANGUAGE_DISPLAY')}</p>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="default"
                className="w-full justify-between text-sm font-normal"
                data-testid="language-button"
              >
                {languages.find(l => l.code === language)?.label || t('SELECT_PLACEHOLDER')}
                <ChevronDown size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
              {languages.map(lang => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`w-full ${language === lang.code ? 'bg-muted font-semibold' : ''}`}
                  data-testid={`dropdown-item-${lang.code}`}
                >
                  {lang.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Auto Translate Toggle */}
        <div className={`flex items-start gap-2 mt-4 ${isCheckboxDisabled ? 'opacity-60 pointer-events-none' : ''}`}>
          <Checkbox
            className="mt-1"
            id="autoTranslate"
            checked={autoTranslate}
            onCheckedChange={setAutoTranslate}
            disabled={isCheckboxDisabled}
          />
          <div className="flex flex-col">
            <span className="font-medium text-sm text">{t('SETTINGS.AUTO_TRANSLATE_TITLE')}</span>
            <span className="text-muted text-sm">{t('SETTINGS.AUTO_TRANSLATE_DESCRIPTION')}</span>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="mt-6 flex justify-end">
          <Button variant="default" size="sm" onClick={handleSave} disabled={!isChanged} data-testid="save-button">
            {t('SETTINGS.SAVE_BUTTON')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
